import { App } from '@capacitor/app'
import { useProject, useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useEffect, useMemo } from 'react'

import { PageArea } from './layout/components/page-area/PageArea'
import { usePendingDocumentCount } from './modules/documents/hooks/usePendingDocumentCount'
import { useUnreadMessageCount } from './modules/messages/hooks/useUnreadMessageCount'
import { useBadgeCount } from './modules/notifications/hooks/useBadgeCount'
import { Navigation } from './modules/shared/components/navigation/Navigation'
import { usePendingTaskCount } from './modules/task/hooks/usePendingTaskCount'
import { useFeatureFlag } from './utils/feature-flag'

import type { NavigationItem } from './modules/shared/components/navigation/Navigation.types'

export const RootNavigation = () => {
  const { pendingTaskCount, checkPendingTaskCount } = usePendingTaskCount()
  const { checkUnreadMessageCount } = useUnreadMessageCount()
  const { pendingDocumentCount } = usePendingDocumentCount()
  const { updateBadgeCount } = useBadgeCount()
  const subjectMember = useSubjectMember()
  const project = useProject()

  useEffect(() => {
    if (subjectMember) {
      checkPendingTaskCount()
      updateBadgeCount()
      checkUnreadMessageCount()
    }

    const listener = App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        checkPendingTaskCount()
        updateBadgeCount()
        checkUnreadMessageCount()
      }
    })

    return () => {
      if (listener) {
        listener.then((handle) => handle.remove())
      }
    }
  }, [subjectMember])

  const careEnabled = useFeatureFlag('care')
  const navigationItems = useMemo(() => {
    const allNavigationItems: NavigationItem[] = [
      {
        label: t({
          id: 'globals.nav.home',
          comment: 'Tabbar label home',
          message: 'Home',
        }),
        icon: ['fal', 'house'],
        iconActive: ['fas', 'house'],
        href: '/home',
      },
      {
        label: t({
          id: 'globals.nav.agenda',
          comment: 'Tabbar label agenda',
          message: 'Agenda',
        }),
        icon: ['fal', 'calendar'],
        iconActive: ['fas', 'calendar'],
        href: '/timeline',
      },
      {
        label: t({
          id: 'globals.nav.task',
          comment: 'Tabbar label task',
          message: 'Taken',
        }),
        icon: ['fal', 'circle-check'],
        iconActive: ['fas', 'circle-check'],
        href: '/task',
        badgeCount: pendingTaskCount > 0 ? pendingTaskCount : undefined,
      },
      {
        label: t({
          id: 'globals.nav.documents',
          comment: 'Tabbar label documents',
          message: 'Bibliotheek',
        }),
        icon: ['fal', 'rectangle-history'],
        iconActive: ['fas', 'rectangle-history'],
        href: '/documents',
        hasNotification: pendingDocumentCount && pendingDocumentCount > 0 ? true : false,
      },
      {
        label: t({
          id: 'globals.nav.care',
          comment: 'Tabbar label care',
          message: 'Welzijn',
        }),
        icon: ['fal', 'note-medical'],
        iconActive: ['fas', 'note-medical'],
        href: '/care',
      },
      {
        label: t({
          id: 'globals.nav.profile',
          comment: 'Tabbar label profile',
          message: 'Profiel',
        }),
        icon: ['fal', 'user'],
        iconActive: ['fas', 'user'],
        href: '/profile',
      },
    ]

    return careEnabled ? allNavigationItems : allNavigationItems.filter((item) => item.href !== '/care')
  }, [project.pid, pendingTaskCount, pendingDocumentCount, careEnabled])

  return (
    <PageArea gridArea='navigation' style={{ zIndex: 1, minWidth: 0 }}>
      <Navigation items={navigationItems} />
    </PageArea>
  )
}
